"use client";
import Image from "next/image";
import React, { useId, useState } from "react";
import Button from "../ui/Button";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { IFaq } from "@/types/faq";

interface FaqProps {
  /** Questions to display, already resolved server-side (see FaqSection). */
  questions: IFaq[];
  stickyNote?: boolean;
}

/**
 * FAQ accordion. Purely presentational: server pages render it through
 * <FaqSection>, the formation page from `formation.faq`.
 *
 * Every answer stays in the DOM (collapsed with CSS, not unmounted), so the
 * full text is in the server-rendered HTML and matches the FAQPage JSON-LD.
 */
const Faq: React.FC<FaqProps> = ({ questions, stickyNote = true }) => {
  const [openItems, setOpenItems] = useState<number[]>([]);
  // Prefix for the question/answer id pairs the accordion's ARIA wiring needs.
  const baseId = useId();

  const toggleItem = (id: number) => {
    setOpenItems((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <section className="w-full bg-white min-h-auto px-4 pt-8 pb-11 sm:py-15 relative">
      <div className="max-w-290.75 mx-auto flex flex-col">
        <h2 className="text-center font-red-hat text-[#1B365E] text-[30px] sm:text-[40px] font-light leading-10 sm:leading-13.25 tracking-[0.3px]">
          FAQ
        </h2>

        <p
          className={cn(
            "mt-4 md:mt-11 mb-8 sm:mb-6 text-[#1D1D1D] text-[20px] leading-7.5 tracking-[0.3px] font-medium",
            !stickyNote && "w-full max-w-214.25 mx-auto",
          )}
        >
          Nous avons apporté les réponses aux questions les plus posées sur les
          formations :
        </p>

        <div className="flex md:flex-row flex-col items-start justify-between gap-8 md:gap-6 w-full">
          <div className={cn("w-full max-w-214.25 flex flex-col gap-4", !stickyNote && "mx-auto")}>
            {questions.map((item) => {
              const isOpen = openItems.includes(item.id);
              const questionId = `${baseId}-question-${item.id}`;
              const answerId = `${baseId}-answer-${item.id}`;
              return (
                <div
                  key={item.id}
                  className="bg-[#EFEFEF] rounded-lg overflow-hidden transition-all duration-300 ease-in-out"
                >
                  <button
                    type="button"
                    id={questionId}
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                    className="w-full flex justify-between items-center px-4 lg:px-6 py-3 sm:py-2.5 bg-transparent border-none cursor-pointer text-left transition-all duration-200 ease-in-out hover:bg-[#D9D9D9]"
                    onClick={() => toggleItem(item.id)}
                  >
                    <span className="text-[#1D1D1D] text-[12px] sm:text-[16px] leading-4 sm:leading-5 font-bold tracking-[0.3px]">
                      {item.question}
                    </span>
                    <motion.span
                      aria-hidden="true"
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-4 text-[25px] md:text-[50px] lg:text-[58px] leading-4 md:leading-11.25 font-red-hat font-semibold text-[#1D1D1D] inline-block h-fit"
                    >
                      {isOpen ? "−" : "+"}
                    </motion.span>
                  </button>

                  {/* 0fr ↔ 1fr grid rows animate the height without unmounting the answer. */}
                  <div
                    id={answerId}
                    role="region"
                    aria-labelledby={questionId}
                    aria-hidden={!isOpen}
                    inert={!isOpen}
                    className={cn(
                      "grid transition-[grid-template-rows,opacity] duration-300 ease-in-out",
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="px-4 lg:px-6 lg:pt-2.5 pb-3 sm:pb-5">
                        <p className="text-[#1D1D1D] text-[12px] leading-5 whitespace-pre-line">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className={cn(
              "min-w-59 w-full md:w-59 border border-[#FFBC42] rounded-2xl pt-6 sm:pt-5.75 pb-6.25 px-8 md:px-5 flex items-center flex-col sticky top-4",
              !stickyNote && "hidden",
            )}
          >
            <Image
              src="/assets/icons/faqs-right-img.svg"
              width={84}
              height={24}
              alt="Questions FAQ"
              title="Questions FAQ"
              className="md:w-21 w-17.25"
            />
            <p className="text-[#1B365E] text-[14px] leading-5 font-normal tracking-[0.3px] text-center py-4">
              Des questions <br className="md:block hidden" />
              sur les formations ?
            </p>
            <Button
              variant="outline-gradient"
              href="/contact"
              className="w-full"
            >
              Nous contacter
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faq;
